import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import {
    SetSelectedLineKeysAction,
    SET_SELECTED_LINE_KEYS,
    TOGGLE_SELECTED_LINE_KEY,
    ToggleSelectedLineKeyAction,
    SELECT_RANGE_TO,
    SelectRangeToAction,
    SET_LINES,
    UpdateLineAction,
    UPDATE_LINE,
    AddLinePlacement,
    ADD_LINE,
    REMOVE_LINES,
    CLONE_LINE,
    CALCULATE_LINE_TOTALS,
    CaclulateAllLinesTotalsAction,
    CALCULATE_ALL_LINES_TOTALS,
    SetBudgetAction,
    SET_BUDGET,
    CALCULATE_BUDGET_TOTALS,
    CaclulateBudgetTotalsAction,
    UpdateBudgetAction,
    UPDATE_BUDGET,
    UPDATE_LINE_QUANTITY,
    UpdateAllLinesAction,
    UPDATE_ALL_LINES,
    UPDATE_LINE_PARAMETER,
    UpdateLineParameterAction,
    UPDATE_LINES_NUMBERS,
    UpdateLinesNumbersAction,
    MOVE_LINES_UP,
    MoveLinesUpAction,
    MoveLinesDownAction,
    MOVE_LINES_DOWN,
    BudgetData,
    SetOpenLinesIndexesAction,
    SET_OPEN_LINES_INDEXES,
    COPY_LINES,
    CUT_LINES,
    PASTE_LINES,
    HYDRATE_CLIPBOARD,
    REMOVE_LINE_CHILDREN,
    UpdateLinesExpressionsValuesAction,
    UPDATE_LINES_EXPRESSIONS_VALUES,
    SET_CALCULATING,
    RECALCULATE_LINE_PARAMETER,
    BudgetState,
    CopyType
} from './type';
import { AtLeast } from 'common/types/Atleast';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { Dispatch } from 'redux';
import { BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import { collectCopiedForest } from './reducer';
import Storage from 'common/services/Storage';
import { STORAGE } from 'Config';

interface ClipboardData {
    copiedLineKeys: string[];
    copyType: CopyType;
    tempLines: BudgetLineDto[];
}

export const setSelectedLineKeys = (
    selectedLineKeys: string[],
    selectionAnchorKey: string | null = null,
): SetSelectedLineKeysAction => ({
    type: SET_SELECTED_LINE_KEYS,
    selectedLineKeys,
    selectionAnchorKey,
});

export const setSelectedLineKey = (selectedLineKey: string | null): SetSelectedLineKeysAction => ({
    type: SET_SELECTED_LINE_KEYS,
    selectedLineKeys: selectedLineKey ? [selectedLineKey] : [],
    selectionAnchorKey: selectedLineKey,
});

export const toggleSelectedLineKey = (lineKey: string): ToggleSelectedLineKeyAction => ({
    type: TOGGLE_SELECTED_LINE_KEY,
    lineKey,
});

export const selectRangeTo = (lineKey: string): SelectRangeToAction => ({
    type: SELECT_RANGE_TO,
    lineKey,
});

export const setLines = (lines: BudgetLineDto[]): any => {
    return async (dispatch: Dispatch) => {
        dispatch({
            type: SET_LINES,
            lines,
        });
        dispatch(updateLinesNumbers());
    };
};

export const setBudget = (budget: BudgetData): SetBudgetAction => ({
    type: SET_BUDGET,
    budget,
});

export const updateLine = (line: AtLeast<BudgetLineDto, 'key'>): UpdateLineAction => ({
    type: UPDATE_LINE,
    line,
});

export const addLine = (lineType: BudgetLineType, placement: AddLinePlacement, lineKey: string | null, newLineKey: string | null): any => {
    return async (dispatch: Dispatch) => {
        dispatch({
            type: ADD_LINE,
            lineType,
            placement,
            lineKey,
            newLineKey,
        });
        dispatch(updateLinesNumbers());
    };
};

export const removeLines = (lineKeys: string[]): any => {
    return async (dispatch: Dispatch) => {
        dispatch({
            type: REMOVE_LINES,
            lineKeys,
        });
        dispatch(updateLinesNumbers());
    };
};

export const removeLine = (lineKey: string): any => removeLines([lineKey]);

export const removeLineChildren = (parentLineKey: string): any => {
    return async (dispatch: Dispatch) => {
        dispatch({
            type: REMOVE_LINE_CHILDREN,
            parentLineKey
        });
        dispatch(updateLinesNumbers());
    };
};

export const cloneLine = (lineKey: string, newLineKey: string): any => {
    return async (dispatch: Dispatch) => {
        dispatch({
            type: CLONE_LINE,
            lineKey,
            newLineKey,
        });
        dispatch(updateLinesNumbers());
        // calculateAllLinesTotalsOnly already runs updateLinesExpressionValues internally (passes 2 and 4)
        dispatch(calculateAllLinesTotalsOnly());
        dispatch(calculateBudgetTotals());
    };
};

// Persists the copied/cut lines to localStorage so they can be pasted into a
// different budget (and survive a page refresh / new tab). Only the copied forest
// is stored, not the whole budget snapshot.
const persistClipboard = (copyType: 'copy' | 'cut', lineKeys: string[], lines: BudgetLineDto[]) => {
    if (!lineKeys.length) {
        Storage.remove(STORAGE.BUDGET_LINES_CLIPBOARD);
        return;
    }
    const data: ClipboardData = {
        copyType,
        copiedLineKeys: lineKeys,
        tempLines: collectCopiedForest(lines, lineKeys),
    };
    Storage.setObject(STORAGE.BUDGET_LINES_CLIPBOARD, data);
};

export const copyLines = (lineKeys: string[]): any => {
    return async (dispatch: Dispatch, getState: () => { budget: BudgetState; }) => {
        dispatch({
            type: COPY_LINES,
            lineKeys,
        });
        persistClipboard('copy', lineKeys, getState().budget.lines);
    };
};

export const cutLines = (lineKeys: string[]): any => {
    return async (dispatch: Dispatch, getState: () => { budget: BudgetState; }) => {
        dispatch({
            type: CUT_LINES,
            lineKeys,
        });
        persistClipboard('cut', lineKeys, getState().budget.lines);
    };
};

const readClipboard = (): ClipboardData | null => {
    const data: ClipboardData | null = Storage.getObject(STORAGE.BUDGET_LINES_CLIPBOARD);
    return data?.copiedLineKeys?.length ? data : null;
};

// Mirrors the localStorage clipboard into the budget slice so the in-memory state
// always reflects the cross-tab source of truth: it sets the copied lines when
// something is stored and clears them otherwise. Dispatched on budget screen mount
// and again right before a paste (to pick up a copy made in another tab after this
// page was opened). Synchronous so callers can read the updated state immediately.
export const hydrateClipboardFromStorage = (): any => {
    return (dispatch: Dispatch) => {
        const data = readClipboard();
        dispatch({
            type: HYDRATE_CLIPBOARD,
            copiedLineKeys: data?.copiedLineKeys ?? [],
            copyType: data?.copyType ?? null,
            tempLines: data?.tempLines ?? [],
        });
    };
};

export const pasteLines = (targetLineKey: string, newKeysByOldKey: Record<string, string>): any => {
    return async (dispatch: Dispatch, getState: () => { budget: BudgetState; }) => {
        const wasCut = getState().budget.copyType === 'cut';
        dispatch({
            type: PASTE_LINES,
            targetLineKey,
            newKeysByOldKey,
        });
        // A pasted "cut" consumes the clipboard (the reducer clears it); drop the
        // persisted copy too so it cannot be pasted again.
        if (wasCut) {
            Storage.remove(STORAGE.BUDGET_LINES_CLIPBOARD);
        }
        dispatch(updateLinesNumbers());
        // calculateAllLinesTotalsOnly already runs updateLinesExpressionValues internally (passes 2 and 4)
        dispatch(calculateAllLinesTotalsOnly());
        dispatch(calculateBudgetTotals());
    };
};

// Debounce: coalesces rapid keystrokes into a single heavy recalc. Save paths
// must call `flushPendingBudgetCalculations` first so state is consistent.
const CALC_DEBOUNCE_MS = 100;
let _pendingCalcTimeout: ReturnType<typeof setTimeout> | null = null;
let _pendingCalcLineKey: string | null = null;
let _pendingCalcDispatch: Dispatch | null = null;

const runPendingCalc = () => {
    if (!_pendingCalcTimeout || !_pendingCalcDispatch) return;
    clearTimeout(_pendingCalcTimeout);
    _pendingCalcTimeout = null;
    const finalLineKey = _pendingCalcLineKey!;
    const dispatch = _pendingCalcDispatch;
    _pendingCalcLineKey = null;
    _pendingCalcDispatch = null;
    dispatch({
        type: CALCULATE_LINE_TOTALS,
        lineKey: finalLineKey
    });
    dispatch(calculateBudgetTotals());
    dispatch(updateLinesExpressionsValues());
    dispatch(calculateAllLinesTotalsOnly());
    dispatch(calculateBudgetTotals());
};

export const calculateLineTotals = (lineKey: string): any => {
    return (dispatch: Dispatch) => {
        _pendingCalcLineKey = lineKey;
        _pendingCalcDispatch = dispatch;
        if (_pendingCalcTimeout) clearTimeout(_pendingCalcTimeout);
        _pendingCalcTimeout = setTimeout(runPendingCalc, CALC_DEBOUNCE_MS);
    };
};

export const calculateAllLinesTotals = (): any => {
    return async (dispatch: Dispatch) => {
        dispatch(calculateAllLinesTotalsOnly());
        dispatch(calculateBudgetTotals());
    };
};

export const calculateAllLinesTotalsOnly = (): CaclulateAllLinesTotalsAction => ({
    type: CALCULATE_ALL_LINES_TOTALS,
});

export const calculateBudgetTotals = (): CaclulateBudgetTotalsAction => ({
    type: CALCULATE_BUDGET_TOTALS,
});

export const updateBudget = (budget: Partial<BudgetData>): UpdateBudgetAction => ({
    type: UPDATE_BUDGET,
    budget,
});

export const updateLineQuantity = (lineKey: string): any => {
    return async (dispatch: Dispatch) => {
        dispatch({
            type: UPDATE_LINE_QUANTITY,
            lineKey
        });
        dispatch(calculateLineTotals(lineKey));
    };
};

export const updateAllLines = (line: Partial<BudgetLineDto>): UpdateAllLinesAction => ({
    type: UPDATE_ALL_LINES,
    line
});

// Per-line debounce for RECALCULATE_LINE_PARAMETER so rapid typing in a parameter
// field doesn't trigger the heavy cross-tree recalc on every keystroke. Save paths
// must call `flushPendingBudgetCalculations` before reading state.
const RECALC_PARAM_DEBOUNCE_MS = 150;
interface PendingRecalc { timer: ReturnType<typeof setTimeout>; dispatch: Dispatch; }
const _pendingRecalcTimers = new Map<string, PendingRecalc>();

const flushPendingRecalcForLine = (lineKey: string) => {
    const pending = _pendingRecalcTimers.get(lineKey);
    if (!pending) return;
    clearTimeout(pending.timer);
    _pendingRecalcTimers.delete(lineKey);
    pending.dispatch({ type: RECALCULATE_LINE_PARAMETER, lineKey });
};

const scheduleRecalculateLineParameter = (dispatch: Dispatch, lineKey: string) => {
    // If a different line has a pending recalc, flush it first so its state stays consistent
    const pendingKeys = Array.from(_pendingRecalcTimers.keys());
    for (const pendingKey of pendingKeys) {
        if (pendingKey !== lineKey) flushPendingRecalcForLine(pendingKey);
    }

    const existing = _pendingRecalcTimers.get(lineKey);
    if (existing) clearTimeout(existing.timer);

    const timer = setTimeout(() => {
        _pendingRecalcTimers.delete(lineKey);
        dispatch({ type: RECALCULATE_LINE_PARAMETER, lineKey });
    }, RECALC_PARAM_DEBOUNCE_MS);
    _pendingRecalcTimers.set(lineKey, { timer, dispatch });
};

/**
 * Synchronously flushes any pending debounced budget calculations. Must be called
 * before reading `state.budget.lines` for persistence (e.g. on save) so the saved
 * state reflects the latest user edits.
 */
export const flushPendingBudgetCalculations = () => {
    // Flush calculateLineTotals debounce
    if (_pendingCalcTimeout) runPendingCalc();
    // Flush every pending RECALCULATE_LINE_PARAMETER
    const pendingKeys = Array.from(_pendingRecalcTimers.keys());
    for (const k of pendingKeys) flushPendingRecalcForLine(k);
};

export const updateLineParameter = (lineKey: string, parameter: AtLeast<BudgetLineParameterDto, 'key'>): any => {
    return (dispatch: Dispatch, getState: () => { budget: BudgetState; }) => {
        // Step 1: Instantly update the parameter value (no recalculation)
        dispatch({
            type: UPDATE_LINE_PARAMETER,
            lineKey,
            parameter,
        });

        // Step 2: Check if recalculation is needed
        const state = getState();
        const line = state.budget.lines.find(l => l.key === lineKey);
        if (!line) return;

        const findParam = (params: BudgetLineParameterDto[], key: string): BudgetLineParameterDto | undefined => {
            for (const p of params) {
                if (p.key === key) return p;
                if (p.children) { const f = findParam(p.children, key); if (f) return f; }
            }
            return undefined;
        };

        const fullParam = parameter.key ? findParam(line.parameters, parameter.key) : undefined;
        const isFormulaType = fullParam && [
            'NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'
        ].includes(fullParam.parameterType ?? '');

        if (!isFormulaType) {
            // Non-formula types (TEXT, IMAGES, etc.) - no recalculation needed
            return;
        }

        // Step 3: Show loading, then defer heavy recalculation behind a debounce
        dispatch({ type: SET_CALCULATING, calculating: true });
        scheduleRecalculateLineParameter(dispatch, lineKey);
    };
};

export const updateLinesNumbers = (): UpdateLinesNumbersAction => ({
    type: UPDATE_LINES_NUMBERS,
});

export const moveLinesUp = (lineKeys: string[]): MoveLinesUpAction => ({
    type: MOVE_LINES_UP,
    lineKeys,
});

export const moveLinesDown = (lineKeys: string[]): MoveLinesDownAction => ({
    type: MOVE_LINES_DOWN,
    lineKeys,
});

export const moveLineUp = (lineKey: string): MoveLinesUpAction => moveLinesUp([lineKey]);
export const moveLineDown = (lineKey: string): MoveLinesDownAction => moveLinesDown([lineKey]);

export const setOpenLinesIndexes = (openLinesIndexes: number[]): SetOpenLinesIndexesAction => ({
    type: SET_OPEN_LINES_INDEXES,
    openLinesIndexes,
});

export const updateLinesExpressionsValues = (): UpdateLinesExpressionsValuesAction => ({
    type: UPDATE_LINES_EXPRESSIONS_VALUES,
});
